import React, { useEffect, useRef } from 'react'
import { getAssetPath } from '../../utils/assetPath'

interface AsciiBackgroundProps {
  isDark: boolean
  isActive: boolean
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>
}

interface RenderConfig {
  renderScale: number
  charSize: number
  frameInterval: number
}

const VIDEO_PATH = getAssetPath('video/blackhole.mp4')
const ASCII_CHARS = ' .,:;i1tfLCG08@'
const ACCENT_CHARS = ' .·:+xH#%@'
const BASE_CHAR_SIZE = 7
const BASE_RENDER_SCALE = 0.6
const BASE_FPS = 18

const getRenderConfig = (): RenderConfig => {
  if (typeof window === 'undefined') {
    return {
      renderScale: BASE_RENDER_SCALE,
      charSize: BASE_CHAR_SIZE,
      frameInterval: 1000 / BASE_FPS,
    }
  }

  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const viewportArea = viewportWidth * viewportHeight

  let renderScale = BASE_RENDER_SCALE
  let charSizeMultiplier = 1
  let fps = BASE_FPS

  if (viewportArea >= 2560 * 1440) {
    renderScale = 0.52
    charSizeMultiplier = 1.12
    fps = 12
  } else if (viewportArea >= 1920 * 1080) {
    renderScale = 0.55
    charSizeMultiplier = 1.22
    fps = 14
  } else if (viewportArea >= 1600 * 900) {
    renderScale = 0.58
    charSizeMultiplier = 1.1
    fps = 15
  }

  return {
    renderScale,
    charSize: BASE_CHAR_SIZE * charSizeMultiplier,
    frameInterval: 1000 / fps,
  }
}

export const AsciiBackground: React.FC<AsciiBackgroundProps> = ({ isDark, isActive, scrollContainerRef }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const sampleCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const charOffsetsRef = useRef<Uint8Array | null>(null)
  const gridSizeRef = useRef({ cols: 0, rows: 0 })
  const renderConfigRef = useRef<RenderConfig>(getRenderConfig())
  const animationFrameRef = useRef<number>(0)
  const renderRef = useRef<(now: number) => void>(() => {})
  const queueFrameRef = useRef<() => void>(() => {})
  const lastFrameRef = useRef(0)
  const isVisibleRef = useRef(true)
  const isActiveRef = useRef(true)
  const scrollYRef = useRef(0)

  useEffect(() => {
    const container = scrollContainerRef?.current
    const target: HTMLDivElement | Window = container ?? window

    const updateScroll = () => {
      scrollYRef.current = container ? container.scrollTop : window.scrollY
    }

    target.addEventListener('scroll', updateScroll, { passive: true })
    updateScroll()

    return () => {
      target.removeEventListener('scroll', updateScroll)
    }
  }, [scrollContainerRef])

  useEffect(() => {
    isActiveRef.current = isActive

    const video = videoRef.current
    if (isActive && isVisibleRef.current) {
      lastFrameRef.current = 0
      queueFrameRef.current()
      video?.play().catch(() => {})
    } else {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = 0
      }
      video?.pause()
    }
  }, [isActive])

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const tryPlay = () => {
      if (isActiveRef.current && isVisibleRef.current) {
        video.play().catch(() => {})
      }
    }

    video.addEventListener('loadeddata', tryPlay)
    video.addEventListener('canplay', tryPlay)

    return () => {
      video.removeEventListener('loadeddata', tryPlay)
      video.removeEventListener('canplay', tryPlay)
    }
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) return

    const getSampleContext = () => {
      if (!sampleCanvasRef.current) {
        sampleCanvasRef.current = document.createElement('canvas')
      }
      return sampleCanvasRef.current.getContext('2d', { willReadFrequently: true })
    }

    const buildCharCache = (cols: number, rows: number) => {
      const total = cols * rows
      const offsets = new Uint8Array(total)
      let seed = 0x9e3779b9 ^ (cols * 2654435761) ^ rows

      for (let i = 0; i < total; i++) {
        seed = (seed * 1664525 + 1013904223) >>> 0
        offsets[i] = seed & 15
      }

      charOffsetsRef.current = offsets
      gridSizeRef.current = { cols, rows }
    }

    const handleResize = () => {
      const renderConfig = getRenderConfig()
      renderConfigRef.current = renderConfig

      const scaledWidth = Math.max(1, Math.floor(window.innerWidth * renderConfig.renderScale))
      const scaledHeight = Math.max(1, Math.floor(window.innerHeight * renderConfig.renderScale))
      canvas.width = scaledWidth
      canvas.height = scaledHeight

      const cols = Math.ceil(canvas.width / renderConfig.charSize)
      const rows = Math.ceil(canvas.height / renderConfig.charSize)
      buildCharCache(cols, rows)

      const sampleCtx = getSampleContext()
      if (sampleCanvasRef.current && sampleCtx) {
        sampleCanvasRef.current.width = cols
        sampleCanvasRef.current.height = rows
        sampleCtx.imageSmoothingEnabled = true
      }
    }

    const queueNextFrame = () => {
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(renderRef.current)
      }
    }

    const drawFallbackFrame = (now: number) => {
      const { charSize } = renderConfigRef.current
      const width = canvas.width
      const height = canvas.height
      const cols = Math.ceil(width / charSize)
      const rows = Math.ceil(height / charSize)
      const time = now * 0.001
      const centerX = cols * 0.5
      const centerY = rows * 0.42

      ctx.fillStyle = isDark ? '#000000' : '#f4f4f5'
      ctx.fillRect(0, 0, width, height)
      ctx.font = `700 ${charSize}px "JetBrains Mono", monospace`
      ctx.textBaseline = 'top'

      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const dx = (c - centerX) / Math.max(1, cols * 0.28)
          const dy = (r - centerY) / Math.max(1, rows * 0.2)
          const dist = Math.sqrt(dx * dx + dy * dy)
          const ring = Math.max(0, 1 - Math.abs(dist - 0.52) * 5)
          const swirl = Math.max(0, Math.sin(time * 2.5 + dist * 12 - Math.atan2(dy, dx) * 1.5))
          const intensity = ring * (0.45 + swirl * 0.55)
          if (intensity < 0.12) continue

          const charSet = intensity > 0.6 ? ACCENT_CHARS : ASCII_CHARS
          const seed = charOffsetsRef.current?.[r * cols + c] ?? 0
          const char = charSet[Math.min(charSet.length - 1, Math.floor(intensity * (charSet.length - 1) + (seed & 1)))]
          const alpha = Math.min(1, intensity * 0.9)

          ctx.fillStyle = isDark ? `rgba(110, 170, 255, ${alpha})` : `rgba(12, 18, 30, ${alpha * 0.82})`

          ctx.fillText(char, c * charSize, r * charSize)
        }
      }
    }

    const render = (now: number) => {
      animationFrameRef.current = 0

      if (!isVisibleRef.current || !isActiveRef.current) return

      const { charSize, frameInterval } = renderConfigRef.current
      if (now - lastFrameRef.current < frameInterval) {
        queueNextFrame()
        return
      }
      lastFrameRef.current = now

      const width = canvas.width
      const height = canvas.height
      const cols = Math.ceil(width / charSize)
      const rows = Math.ceil(height / charSize)
      const video = videoRef.current
      const sampleCtx = getSampleContext()

      if (
        !sampleCtx ||
        !sampleCanvasRef.current ||
        !video ||
        video.readyState < 2 ||
        !video.videoWidth ||
        !video.videoHeight
      ) {
        drawFallbackFrame(now)
        queueNextFrame()
        return
      }

      if (gridSizeRef.current.cols !== cols || gridSizeRef.current.rows !== rows || !charOffsetsRef.current) {
        buildCharCache(cols, rows)
      }

      if (sampleCanvasRef.current.width !== cols || sampleCanvasRef.current.height !== rows) {
        sampleCanvasRef.current.width = cols
        sampleCanvasRef.current.height = rows
      }

      const videoAspect = video.videoWidth / video.videoHeight
      const targetAspect = cols / rows
      let sourceX = 0
      let sourceY = 0
      let sourceWidth = video.videoWidth
      let sourceHeight = video.videoHeight

      if (videoAspect > targetAspect) {
        sourceWidth = video.videoHeight * targetAspect
        sourceX = (video.videoWidth - sourceWidth) * 0.5
      } else {
        sourceHeight = video.videoWidth / targetAspect
        sourceY = (video.videoHeight - sourceHeight) * 0.5
      }

      sampleCtx.clearRect(0, 0, cols, rows)
      sampleCtx.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, cols, rows)

      const frame = sampleCtx.getImageData(0, 0, cols, rows).data
      const time = now * 0.001
      const timeTick = Math.floor(time * 10)
      const scrollOffset = scrollYRef.current * 0.025 * renderConfigRef.current.renderScale
      const waveOffset = Math.sin(time * 0.7) * charSize * 0.18
      const charOffsets = charOffsetsRef.current
      const centerX = cols * 0.5
      const centerY = rows * 0.42

      ctx.fillStyle = isDark ? '#000000' : '#f4f4f5'
      ctx.fillRect(0, 0, width, height)
      ctx.font = `700 ${charSize}px "JetBrains Mono", monospace`
      ctx.textBaseline = 'top'

      for (let r = 0; r < rows; r++) {
        const drawY = r * charSize - scrollOffset + waveOffset

        for (let c = 0; c < cols; c++) {
          const pixelIndex = (r * cols + c) * 4
          const red = frame[pixelIndex]
          const green = frame[pixelIndex + 1]
          const blue = frame[pixelIndex + 2]
          const alphaChannel = frame[pixelIndex + 3] / 255

          if (alphaChannel <= 0.01) continue

          const luma = (0.2126 * red + 0.7152 * green + 0.0722 * blue) / 255
          const maxChannel = Math.max(red, green, blue) / 255
          const minChannel = Math.min(red, green, blue) / 255
          const saturation = Math.max(0, maxChannel - minChannel)
          const warmth = Math.max(0, (red - blue) / 255)

          const rawEnergy = Math.max(luma * 1.2, maxChannel * 0.95 + saturation * 0.42)
          const dx = (c - centerX) / Math.max(1, cols * 0.34)
          const dy = (r - centerY) / Math.max(1, rows * 0.26)
          const dist = Math.sqrt(dx * dx + dy * dy)
          const ringBoost = Math.max(0, 1 - Math.abs(dist - 0.52) * 5.5) * 0.14
          const coreCut = dist < 0.26 ? (0.26 - dist) * 0.42 : 0
          const energy = isDark
            ? Math.min(1, Math.max(0, rawEnergy + ringBoost - coreCut - 0.05))
            : Math.min(1, Math.max(0, rawEnergy - 0.05))

          if (energy < 0.08) continue

          const seed = charOffsets?.[r * cols + c] ?? 0
          const shimmer = 0.88 + 0.12 * Math.sin(time * 9 + c * 0.18 + r * 0.24 + seed)
          const finalAlpha = Math.min(1, Math.pow(energy, 1.15) * shimmer)

          if (finalAlpha < 0.06) continue

          const charSet = saturation > 0.18 || warmth > 0.14 || energy > 0.7 ? ACCENT_CHARS : ASCII_CHARS
          const densityIndex = Math.min(charSet.length - 1, Math.floor(energy * (charSet.length - 1)))
          const animatedIndex = Math.min(charSet.length - 1, densityIndex + (((seed + timeTick) & 7) === 0 ? 1 : 0))
          const char = charSet[animatedIndex]

          if (isDark) {
            const colorRed = Math.min(255, Math.round(red * 1.22 + 18))
            const colorGreen = Math.min(255, Math.round(green * 1.12 + 16))
            const colorBlue = Math.min(255, Math.round(blue * 1.35 + 28))
            const monochrome = Math.round(200 + energy * 55)

            ctx.fillStyle =
              saturation > 0.12 || warmth > 0.08
                ? `rgba(${colorRed}, ${colorGreen}, ${colorBlue}, ${finalAlpha})`
                : `rgba(${monochrome}, ${monochrome}, 255, ${finalAlpha * 0.92})`
          } else {
            const colorRed = Math.round(8 + red * 0.26)
            const colorGreen = Math.round(8 + green * 0.22)
            const colorBlue = Math.round(12 + blue * 0.3)

            ctx.fillStyle =
              saturation > 0.12 || warmth > 0.08
                ? `rgba(${Math.min(92, colorRed)}, ${Math.min(86, colorGreen)}, ${Math.min(104, colorBlue)}, ${finalAlpha * 0.88})`
                : `rgba(10, 12, 18, ${finalAlpha * 0.9})`
          }

          ctx.fillText(char, c * charSize, drawY)
        }
      }

      queueNextFrame()
    }

    const handleVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible'
      isVisibleRef.current = isVisible
      const video = videoRef.current

      if (isVisible && isActiveRef.current) {
        lastFrameRef.current = 0
        queueNextFrame()
        video?.play().catch(() => {})
      } else {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = 0
        }
        video?.pause()
      }
    }

    renderRef.current = render
    queueFrameRef.current = queueNextFrame

    window.addEventListener('resize', handleResize)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    handleResize()
    queueNextFrame()

    return () => {
      window.removeEventListener('resize', handleResize)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = 0
      }
    }
  }, [isDark])

  return (
    <div className='fixed inset-0 z-0 pointer-events-none overflow-hidden'>
      <video
        ref={videoRef}
        src={VIDEO_PATH}
        autoPlay
        loop
        muted
        playsInline
        preload='auto'
        className='hidden'
        aria-hidden='true'
      />
      <canvas ref={canvasRef} className='w-full h-full transition-opacity duration-1000' />
      <div
        className='absolute inset-0 opacity-95'
        style={{
          backgroundImage: isDark
            ? 'radial-gradient(circle at center 42%, rgba(0, 0, 0, 0.82) 0%, rgba(0, 0, 0, 0.7) 7%, rgba(0, 0, 0, 0.15) 17%, rgba(0, 0, 0, 0) 24%), radial-gradient(ellipse at center, rgba(0, 0, 0, 0) 24%, rgba(0, 0, 0, 0.78) 90%)'
            : 'radial-gradient(ellipse at center, rgba(244, 244, 245, 0) 28%, rgba(244, 244, 245, 0.94) 92%)',
        }}
      />
    </div>
  )
}
